import { useCallback, useEffect, useState } from 'react';
import { Encounter, Observation, Patient } from '@medplum/fhirtypes';
import { MedplumClient, resolveId } from '@medplum/core';
import { useSubscription } from '@medplum/react';
import { loadEncounterPsychomotorDevelopment } from '@/pages/ainigma/patient/examination/examination.helpers';
import { PsychomotorDevelopmentType } from '@/types/psychomotor-development.types';

export interface EncounterData {
  encounter: Encounter;
  data: Record<PsychomotorDevelopmentType, boolean | null>;
}

// Reverse mapping from observation code to PsychomotorDevelopmentType
// This matches the PSYCHOMOTOR_DEVELOPMENT_CODE_MAP in examination.helpers.ts
const CODE_TO_TYPE_MAP: Record<
  string,
  { type: PsychomotorDevelopmentType; invertBoolean?: boolean }
> = {
  '23397005': { type: 'age-compatible' },
  '225583004': { type: 'social-smile' },
  '840673007': { type: 'head-support' },
  '165245003': { type: 'independent-walking' },
  '1197010003': { type: 'speech' },
  '315245004': { type: 'school-performance', invertBoolean: true },
};

export const usePsychomotorDevelopmentData = (
  patient: Patient | undefined,
  encounters: Encounter[],
  encountersLoading: boolean,
  medplum: MedplumClient
): { encountersData: EncounterData[]; loading: boolean } => {
  const [encountersData, setEncountersData] = useState<EncounterData[]>([]);
  const [loading, setLoading] = useState(true);

  const loadAllData = useCallback(async () => {
    if (!patient || encountersLoading || encounters.length === 0) {
      setLoading(false);
      setEncountersData([]);
      return;
    }

    setLoading(true);
    try {
      const dataPromises = encounters.map(async (encounter) => {
        const { items } = await loadEncounterPsychomotorDevelopment(
          medplum,
          patient as Patient,
          encounter
        );

        // Create a map of type -> value
        const dataMap: Record<PsychomotorDevelopmentType, boolean | null> = {
          'age-compatible': null,
          'social-smile': null,
          'head-support': null,
          'independent-walking': null,
          speech: null,
          'school-performance': null,
        };

        items.forEach((item) => {
          dataMap[item.type] = item.value;
        });

        return {
          encounter,
          data: dataMap,
        };
      });

      const results = await Promise.all(dataPromises);
      setEncountersData(results);
    } catch (error) {
      console.error('Error loading psychomotor development data:', error);
    } finally {
      setLoading(false);
    }
  }, [patient, encounters, encountersLoading, medplum]);

  useEffect(() => {
    loadAllData();
  }, [loadAllData]);

  // Subscribe to Observation changes for psychomotor development
  useSubscription(
    patient?.id
      ? `Observation?subject=Patient/${patient.id}&category=survey`
      : '',
    (bundle) => {
      const observation = bundle.entry?.find(
        (o) => o.resource?.resourceType === 'Observation'
      )?.resource as Observation | undefined;

      if (!observation?.id || !observation.encounter) {
        return;
      }

      // Extract encounter ID from reference (format: "Encounter/123")
      const encounterId = resolveId(observation.encounter);
      const affectedEncounter = encounters.find((e) => e.id === encounterId);

      if (!affectedEncounter) {
        return;
      }

      // Extract observation code to find the corresponding type
      const observationCode = observation.code?.coding?.[0]?.code;
      if (!observationCode) {
        return;
      }

      // Find the type and invertBoolean flag for this code
      const codeInfo = CODE_TO_TYPE_MAP[observationCode];
      if (!codeInfo) {
        return;
      }

      // Extract valueBoolean from observation
      // Apply invertBoolean logic if needed (same as in loadEncounterPsychomotorDevelopment)
      let value: boolean | null = null;
      if (observation.valueBoolean !== undefined) {
        value = codeInfo.invertBoolean
          ? !observation.valueBoolean
          : observation.valueBoolean;
      }

      // Update only the specific type for the specific encounter
      setEncountersData((prev) =>
        prev.map((encounterData) => {
          if (encounterData.encounter.id === affectedEncounter.id) {
            return {
              ...encounterData,
              data: {
                ...encounterData.data,
                [codeInfo.type]: value,
              },
            };
          }
          return encounterData;
        })
      );
    }
  );

  return { encountersData, loading };
};

